/**
 * Concluir o módulo e repassar ao atendimento (item 6 da Amanda) — o contrato e as consultas.
 *
 * Três leituras e quatro atos moram aqui:
 *
 * · **O painel do repasse** (console): por módulo, o que trava o botão e a **razão por escrito**
 *   ("2 de 3 Recursos Plus ainda em aberto"). A tela nunca decide sozinha se o botão libera: o
 *   servidor manda `pode_repassar` e `motivos`, e o `POST` ainda confere de novo — o botão cinza
 *   não é a única defesa.
 * · **Os termos de encerramento** são lidos pela tela Contrato (`contratoDoCliente.ts`); aqui só se
 *   invalida a lista quando um repasse gera um termo novo.
 * · **O acesso aos Chamados** (cliente): bloqueado até o primeiro repasse. É o que o menu e a tela
 *   de Chamados leem; a recusa de verdade é da API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { enviarJson, pedirJson } from "./api";
import { CHAVE_RAIZ_DO_CONSOLE } from "./console";
import { CHAVE_DOS_TERMOS } from "./contratoDoCliente";
import { CHAVE_MINHA_ESTEIRA } from "./implantacoes";
import { CHAVE_RAIZ_DO_JIRA } from "./jira";

export const PERMISSAO_DE_REPASSAR = "implantacao:repassar";
export const PERMISSAO_DE_VER_O_REPASSE = "console:ver";

/* ------------------------------------------------------------------------------- esquemas */

const esquemaCondicao = z.object({
  chave: z.string(),
  rotulo: z.string(),
  cumprida: z.boolean(),
  detalhe: z.string(),
});

const esquemaRecurso = z.object({
  chave: z.string(),
  nome: z.string(),
  issue: z.string().nullable(),
  status: z.string(),
  situacao: z.enum(["concluido", "cancelado", "em_aberto"]),
  situacao_rotulo: z.string(),
});

const esquemaTreinamento = z.object({
  reuniao_id: z.string(),
  assunto: z.string(),
  data: z.string(),
  com_ata: z.boolean(),
  com_gravacao: z.boolean(),
  sugerido: z.boolean(),
});

const esquemaChamado = z.object({
  ticket_id: z.string(),
  numero: z.number(),
  assunto: z.string(),
  estado: z.string(),
  chave_no_atd: z.string().nullable(),
  sugerido: z.boolean(),
});

const esquemaResumo = z.object({
  id: z.string(),
  modulo_chave: z.string(),
  modulo_nome: z.string(),
  repassado_em: z.string(),
  repassado_por: z.string(),
  consultor_do_modulo: z.string().nullable(),
  observacao: z.string().nullable(),
  liberou_chamados: z.boolean(),
  ultimo: z.boolean(),
  termo: z.object({ nome: z.string(), tamanho: z.number() }),
  jira: z.object({
    tarefa_fechada_em: z.string().nullable(),
    tarefa_erro: z.string().nullable(),
    termo_anexado_em: z.string().nullable(),
    termo_anexo_erro: z.string().nullable(),
    tentativas: z.number(),
    pendente: z.boolean(),
  }),
  emails: z.object({
    destinatarios: z.number(),
    enviados: z.number(),
    pendentes: z.number(),
    falhas: z.number(),
    ultimo_erro: z.string().nullable().default(null),
  }),
});

const esquemaModulo = z.object({
  chave: z.string(),
  nome: z.string(),
  tarefa: z.string().nullable(),
  etapa_id: z.string().nullable(),
  etapa: z.string().nullable(),
  responsavel: z.string().nullable(),
  observado_em: z.string().nullable(),
  rejeitado: z.boolean(),
  repassado: esquemaResumo.nullable(),
  recursos: z.array(esquemaRecurso),
  condicoes: z.array(esquemaCondicao),
  pode_repassar: z.boolean(),
  motivos: z.array(z.string()),
  liberaria_chamados: z.boolean(),
  ultimo: z.boolean(),
  treinamentos: z.array(esquemaTreinamento),
  chamados: z.array(esquemaChamado),
});

export const esquemaDoRepasse = z.object({
  implantacao_id: z.string(),
  estado: z.string(),
  kickoff_fechado: z.boolean(),
  chamados_liberados: z.boolean(),
  epico: z.object({
    chave: z.string().nullable(),
    concluido: z.boolean(),
    pendencia: z.string().nullable(),
  }),
  modulos: z.array(esquemaModulo),
  recursos_sem_modulo: z.array(esquemaRecurso),
  /* O GeoTask não respondeu por alguma tarefa: ela aparece com o último status gravado. */
  aviso_geotask: z.string().nullable().default(null),
});

export const esquemaDoAcessoAosChamados = z.object({
  liberado: z.boolean(),
  motivo: z.string().nullable(),
});

export type RepasseDaImplantacao = z.infer<typeof esquemaDoRepasse>;
export type ModuloDoRepasse = z.infer<typeof esquemaModulo>;
export type RecursoDoRepasse = z.infer<typeof esquemaRecurso>;
export type ResumoDoRepasse = z.infer<typeof esquemaResumo>;
export type AcessoAosChamados = z.infer<typeof esquemaDoAcessoAosChamados>;

export interface PedidoDeRepasse {
  observacao: string | null;
  treinamentos: string[];
  chamados: string[];
}

/* ----------------------------------------------------------------------------- consultas */

export const chaveDoRepasse = (implantacaoId: string) =>
  [...CHAVE_RAIZ_DO_CONSOLE, "repasse", implantacaoId] as const;
export const CHAVE_DO_ACESSO_AOS_CHAMADOS = ["chamados", "acesso"] as const;

/** Quanto esperar antes de reler o painel enquanto o repasse ainda está sendo cumprido. */
export const INTERVALO_DO_REPASSE_EM_ANDAMENTO = 3_000;

/**
 * Reler sozinho enquanto o que o repasse dispara depois do commit — a fila do Jira e a fila de
 * e-mail — ainda não deixou rastro. Termina sozinho: com o passo cumprido, ou com o motivo da
 * falha escrito (pendência é espera, e o botão de reprocessar assume dali), não há mais o que
 * esperar. Sem isso o cartão ficaria em "aguardando a fila" até alguém recarregar a página.
 */
export function intervaloDeAtualizacao(dados: RepasseDaImplantacao | undefined): number | false {
  const aguardando = dados?.modulos.some(({ repassado: r }) => {
    if (!r) return false;
    const jiraSemRastro =
      r.jira.pendente && !r.jira.tarefa_erro && !r.jira.termo_anexo_erro;
    return jiraSemRastro || r.emails.pendentes > 0;
  });
  return aguardando ? INTERVALO_DO_REPASSE_EM_ANDAMENTO : false;
}

export function useRepasseDaImplantacao(implantacaoId: string, habilitado: boolean) {
  return useQuery<RepasseDaImplantacao>({
    queryKey: chaveDoRepasse(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(
        `/implantacoes/${encodeURIComponent(implantacaoId)}/repasse`,
        esquemaDoRepasse,
        { signal },
      ),
    enabled: habilitado && implantacaoId.length > 0,
    retry: false,
    refetchInterval: (consulta) => intervaloDeAtualizacao(consulta.state.data),
  });
}

function invalidarOQueORepasseMuda(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_CONSOLE });
  void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_JIRA });
  void cliente.invalidateQueries({ queryKey: CHAVE_MINHA_ESTEIRA });
  void cliente.invalidateQueries({ queryKey: CHAVE_DOS_TERMOS });
  void cliente.invalidateQueries({ queryKey: CHAVE_DO_ACESSO_AOS_CHAMADOS });
}

/** O ato: concluir o módulo e repassá-lo. Bloqueado, o servidor devolve `409` com as razões. */
export function useRepassarModulo(implantacaoId: string, moduloChave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeRepasse) =>
      enviarJson(
        `/implantacoes/${encodeURIComponent(implantacaoId)}/modulos/${encodeURIComponent(moduloChave)}/repassar`,
        esquemaResumo,
        corpo,
      ),
    onSuccess: () => invalidarOQueORepasseMuda(cliente),
  });
}

/** Repete só o que o Jira deixou pendente (anexar o termo, fechar a tarefa). */
export function useReprocessarRepasse() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (repasseId: string) =>
      enviarJson(`/repasses/${encodeURIComponent(repasseId)}/reprocessar`, esquemaResumo, undefined),
    onSuccess: () => invalidarOQueORepasseMuda(cliente),
  });
}

/** Reenvia a formalização a quem não a recebeu. */
export function useReenviarEmailsDoRepasse() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (repasseId: string) =>
      enviarJson(
        `/repasses/${encodeURIComponent(repasseId)}/emails/reenviar`,
        esquemaResumo,
        undefined,
      ),
    onSuccess: () => invalidarOQueORepasseMuda(cliente),
  });
}

/** O cliente já pode abrir chamados? Bloqueado até o primeiro repasse de módulo. */
export function useAcessoAosChamados(habilitado: boolean) {
  return useQuery<AcessoAosChamados>({
    queryKey: CHAVE_DO_ACESSO_AOS_CHAMADOS,
    queryFn: ({ signal }) =>
      pedirJson("/chamados/acesso", esquemaDoAcessoAosChamados, { signal }),
    enabled: habilitado,
    retry: false,
    staleTime: 60_000,
  });
}

/* ----------------------------------------------------------------------- apresentação */

/** `12.345` bytes -> "12 KB"; abaixo de 1 KB, "1 KB": o termo nunca é menor que isso. */
export function tamanhoLegivel(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** O que o repasse vai disparar, na ordem em que acontece — para a confirmação dizer antes. */
export function efeitosDoRepasse(modulo: ModuloDoRepasse): string[] {
  const tarefa = modulo.tarefa ?? "do módulo";
  const efeitos = [
    `Termo de encerramento do ${modulo.nome} gerado em PDF e guardado na conta do cliente`,
    `Termo anexado à tarefa ${tarefa} e a tarefa levada de Acompanhamento para Concluído no Jira`,
    "Formalização enviada por e-mail a todas as pessoas da conta, com o termo em anexo",
    modulo.liberaria_chamados
      ? "Menu Chamados liberado para o cliente (este é o primeiro repasse)"
      : "Menu Chamados já estava liberado: a frase sobre ele não entra no e-mail",
  ];
  efeitos.push(
    modulo.ultimo
      ? "Último módulo: o épico vai para Concluído e a conta passa à carteira do atendimento"
      : "Épico continua aberto: ainda há módulo em implantação",
  );
  return efeitos;
}
